import { ErrorMessage } from '@components/error-message'
import { useSDK } from '@features/app-deps-provider'
import { useAuthClient, useAuthState, useSetRoute } from '@features/app-hooks'
import { RadioSelect } from '@features/ui-components'
import { OrganizationMembership } from '@st/sdk'
import { Button, FullPageCard } from '@st/theme'
import { usePromise } from '@util/promise'
import { useEffect } from 'react'
import { P, match } from 'ts-pattern'
/**
 * After the user is logged in, this component handles:
 * - Fetching the user's organization memberships.
 * - Deciding if we need to display multiple org selection or redirect automatically
 * - Displaying an error or no-orgs message if needed
 */
export function OrganizationSelectorPage() {
  const sdk = useSDK()
  const auth = useAuthClient()
  const authState = useAuthState()

  const membershipsResponse = usePromise(
    () => sdk.send({ type: 'organizations/getMyOrganizationMemberships' }),
    [sdk]
  )

  return (
    <FullPageCard>
      {match(membershipsResponse)
        .with(
          { status: 'fulfilled', value: { memberships: P.when((arr) => arr.length == 1) } },
          (resp) => <RedirectToOrganization membership={resp.value.memberships[0]} />
        )
        .with(
          { status: 'fulfilled', value: { memberships: P.when((arr) => arr.length > 1) } },
          (resp) => <OrganizationSelector memberships={resp.value.memberships} />
        )
        .with({ status: 'rejected' }, () => <ErrorMessage message="Failed to load organizations" />)
        .with({ status: 'pending' }, () => <></>)
        .otherwise(() => (
          <div className="flex flex-col gap-2">
            <h2 className="mb-4 text-center">
              You are not in any organizations. You must create one or be invited to one.
            </h2>
            <p className="text-center text-sm">Logged in as {authState.user?.email}</p>
            <Button variant="primary" onClick={() => auth.logout()}>
              Log out
            </Button>
          </div>
        ))}
    </FullPageCard>
  )
}

/**
 * Allows the user to select which organization they want to access.
 */
function OrganizationSelector({ memberships }: { memberships: OrganizationMembership[] }) {
  const setRoute = useSetRoute()

  type Option = { key: string; label: string }

  const options: Option[] = memberships.map((m) => {
    return { key: m.organization.slug, label: m.organization.name }
  })

  return (
    <div className="flex flex-col gap-10">
      <h2>Please select an organization</h2>

      <RadioSelect
        options={options}
        value={undefined}
        onChange={(key) => setRoute({ name: 'folder_list', organizationSlug: key })}
      />
    </div>
  )
}

/**
 * Redirects the user to the given organization's route.
 */
function RedirectToOrganization({ membership }: { membership: OrganizationMembership }) {
  const setRoute = useSetRoute()

  useEffect(() => {
    setRoute({
      name: 'folder_list',
      organizationSlug: membership.organization.slug
    })
  }, [membership.organization.slug])

  return null
}
